import React, { FC, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { FlyoutContext } from "@app/shared/Flyouts";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { parseQuerystring } from "@app/shared/utility/parseQuerystring";
import { querystringify } from "@app/shared/utility/querystringify";

/** The shape of possible query string parameters. */
export interface RegionQueryShape {
  active?: string;
  approved?: string;
  opportunity?: string;
  pending?: string;
}

interface RegionStatusFilterProps {
  availableRegionSetStatusTypes: RegionSetStatusType[];
  showRegionStatusSelectors: boolean;
}

const RegionStatusFilter: FC<RegionStatusFilterProps> = (props: RegionStatusFilterProps) => {
  const location = useLocation();
  const history = useHistory();
  const actionMenuContext = useContext(FlyoutContext);

  // use the query to get the initial states
  const query = parseQuerystring(location.search) as RegionQueryShape;
  const isQueryEmpty = Object.keys(query).length === 0;
  const [currentQuery, setCurrentQuery] = useState({} as RegionQueryShape); //since the query changes until it becomes constant.

  const [active, setActive] = useState(query.active === "true" || isQueryEmpty);
  const [pending, setPending] = useState(query.pending === "true" || isQueryEmpty);
  const [approved, setApproved] = useState(query.approved === "true" || isQueryEmpty);
  const [opportunity, setOpportunity] = useState(query.opportunity === "true");

  useEffect(() => {
    if (!active && !pending && !approved && !opportunity) {
      updateServiceAreaCheckboxes(true);
    }
  }, [active, pending, approved]);

  if (JSON.stringify(currentQuery) != JSON.stringify(query))
    setCurrentQuery(query);

  useEffect(() => {
    setActive(currentQuery.active === "true" || isQueryEmpty);
    setPending(currentQuery.pending === "true" || isQueryEmpty);
    setApproved(currentQuery.approved === "true" || isQueryEmpty);
    setOpportunity(currentQuery.opportunity === "true");
  }, [currentQuery]);

  const allFiltersUndefinedOrFalse = (): boolean => {
    return !active && !pending && !approved && !opportunity;
  }

  const isServiceAreaChecked = () => {
    return allFiltersUndefinedOrFalse() || active || pending || approved;
  };

  //due to the added complications of compare mode, decided to never disable active, pending or approved
  //but the constant values still make the code cleaner
  const disableActive = !active && props.availableRegionSetStatusTypes.indexOf(RegionSetStatusType.Active) === -1 && props.availableRegionSetStatusTypes.indexOf(RegionSetStatusType.Unknown) === -1;
  const disablePending = !pending && props.availableRegionSetStatusTypes.indexOf(RegionSetStatusType.Pending) === -1;
  const disableApproved = !approved && props.availableRegionSetStatusTypes.indexOf(RegionSetStatusType.Approved) === -1;
  const disableOpportunity = !opportunity && props.availableRegionSetStatusTypes.indexOf(RegionSetStatusType.Opportunity) === -1;
  const disableCurrentServiceArea = !isServiceAreaChecked() //allows for the first click into the project to open at the undefined service area
    && disableActive
    && disablePending
    && disableApproved
    && !disableOpportunity;

  const updateQuery = () => {
    const existingQuery = parseQuerystring(location.search);
    
    const query = {
      ...existingQuery,  // ✅ Preserve all existing URL parameters
      active: active.toString(),
      approved: approved.toString(),
      opportunity: opportunity.toString(),
      pending: pending.toString(),
    };
    
    // If we somehow end up with nothing selected, apply all current service area filters.
    if (query.opportunity !== true.toString() &&
      query.active !== true.toString() &&
      query.approved !== true.toString() &&
      query.pending !== true.toString()) {
      query.active = query.approved = query.pending = true.toString();
    }

    const qs = querystringify(query as { [key: string]: unknown });
    history.push({
      ...history.location,
      search: qs,
    });
    actionMenuContext.handleCloseFlyout();
  }

  const updateServiceAreaCheckboxes = (value: boolean) => {
    setActive(value);
    setPending(value);
    setApproved(value);
  }

  return (
    <>
      <div className="col-12 mt10 px-2">
        <TooltipWrapper text={disableCurrentServiceArea ? "There are no counties defined in current service area at this level" : ""} position="left">
          <div>
            <QESRadioButton
              label="Current Service Area"
              disabled={disableCurrentServiceArea}
              onChange={disableCurrentServiceArea ?
                null :
                (e) => {
                  if (e.target.checked) {
                    updateServiceAreaCheckboxes(true); setOpportunity(false);
                  }
                }
              }
              checked={active || pending || approved || (!active && !pending && !approved) && !opportunity}
              testId="region-status-filter__current-service-area"
              name="region-type"
            />
          </div>
        </TooltipWrapper>
      </div>
      {props.showRegionStatusSelectors && isServiceAreaChecked() &&
        <React.Fragment>
          <div className="col-10 offset-2 px-2">
            <QESCheckbox
              containerClassName="mt-0 mb-0"
              label={<>&nbsp;{RegionSetStatusType.Active}</>}
              className="mt-0 me-1"
              checked={active}
              onChange={(e) => { setActive(e.target.checked) }}
              testId="region-status-filter__active"
            />
          </div>
          <div className="col-10 offset-2 px-2">
            <QESCheckbox
              containerClassName="mt-0 mb-0"
              label={<>&nbsp;{RegionSetStatusType.Pending}</>}
              className="mt-0 me-1"
              checked={pending}
              onChange={(e) => { setPending(e.target.checked) }}
              testId="region-status-filter__pending"
            />
          </div>
          <div className="col-10 offset-2 px-2">
            <QESCheckbox
              containerClassName="mt-0 mb-0"
              label={<>&nbsp;{RegionSetStatusType.Approved}</>}
              className="mt-0 me-1"
              checked={approved}
              onChange={(e) => { setApproved(e.target.checked) }}
              testId="region-status-filter__approved"
            />
          </div>
        </React.Fragment>
      }
      <div className="col-12 mb10 px-2">
        <TooltipWrapper text={disableOpportunity ? "There are no opportunities to expand at this level" : ""} position="left" sticky={true}>
          <QESRadioButton
            label="Opportunities to Expand"
            disabled={disableOpportunity}
            checked={opportunity}
            onChange={(e) => {
              setOpportunity(e.target.checked);
              if (e.target.checked) {
                updateServiceAreaCheckboxes(false);
              }
            }}
            testId="region-status-filter__opportunity"
          />
        </TooltipWrapper>
      </div>
      <div className="px-1">
        <QESButton qesType="primary" qesTestId="region-status-filter__apply" onClick={updateQuery} className="col-12">
          Apply
        </QESButton>
      </div>
    </>
  );
}

export default RegionStatusFilter;
